import type {
  AnalyticsInterval,
  AnalyticsMetricName,
  StaffAnalyticsMetric,
} from "@lafayette/schemas";

import { countText, durationText, moneyText } from "../format";
import { METRIC_INFO, seriesLabel } from "./metric-catalog";

// What the Explore chart draws and says, computed from the answer of the API. Pure, so a test proves each rule.

type Unit = StaffAnalyticsMetric["unit"];
type Series = StaffAnalyticsMetric["series"][number];

const COMPACT_RUPEES = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  notation: "compact",
  maximumFractionDigits: 1,
});

/** A number of a metric in words: `2 400` seconds as "40 min", `475000` paise as "₹4,750", a ratio as "3.5". */
export function valueText(value: number, unit: Unit): string {
  if (unit === "seconds") return durationText(value);
  if (unit === "minor_inr") return moneyText(Math.round(value));
  if (unit === "ratio") return String(Math.round(value * 10) / 10);
  return countText(Math.round(value));
}

/** The same for an axis label: short. */
export function axisText(value: number, unit: Unit): string {
  if (unit === "minor_inr") return COMPACT_RUPEES.format(value / 100);
  return valueText(value, unit);
}

/** The change against the previous period: "+112%", "−8%", or "no change". `null` when it cannot be computed. */
export function changeText(change: number | null): string | null {
  if (change === null) return null;
  const percent = Math.round(change * 100);
  if (percent === 0) return "no change";
  return `${percent > 0 ? "+" : "−"}${Math.abs(percent)}%`;
}

/** Whole numbers of a count, to compare: the difference in words ("+2"). */
export function differenceText(value: number, previous: number): string {
  const difference = value - previous;
  if (difference === 0) return "no change";
  return `${difference > 0 ? "+" : "−"}${countText(Math.abs(difference))}`;
}

export type BarSegment = Readonly<{ key: string; value: number; y0: number; y1: number }>;

export type BarColumn = Readonly<{
  t: string;
  partial: boolean;
  /** The value of the whole bucket, or `null` when it cannot be said: a ratio is not the sum of its splits, and the API sent no weighted value. */
  total: number | null;
  segments: readonly BarSegment[];
  previous: number | null;
}>;

export type ChartModel = Readonly<{
  columns: readonly BarColumn[];
  keys: readonly string[];
  max: number;
  /** Stacked bars add up (a count, a time); a ratio is drawn side by side, because the sum of two ratios means nothing. */
  stacked: boolean;
}>;

/** The series in order of size (biggest first, `other` last), so the biggest sits at the bottom of a stack and gets the first colour. */
function keysBySize(series: readonly Series[]): readonly string[] {
  const size = (entry: Series) => entry.points.reduce((sum, point) => sum + point.value, 0);
  return [...series]
    .sort((a, b) => {
      if (a.key === "other" || b.key === "other") return a.key === "other" ? 1 : -1;
      return size(b) - size(a) || a.key.localeCompare(b.key);
    })
    .map((entry) => entry.key);
}

/** The value of a bucket of a metric that does not add up: the weighted value of the API, or the one series that there is. Never a sum of splits. */
function unsplitValue(
  metric: StaffAnalyticsMetric,
  seriesCount: number,
  index: number,
): number | null {
  const combined = metric.combined?.[index];
  if (combined !== undefined) return combined.value;
  return seriesCount === 1 ? (metric.series[0]?.points[index]?.value ?? null) : null;
}

export function chartModel(metric: StaffAnalyticsMetric): ChartModel {
  const stacked = metric.unit !== "ratio";
  const keys = keysBySize(metric.series);
  const byKey = new Map(metric.series.map((entry) => [entry.key, entry.points]));
  const count = metric.series[0]?.points.length ?? 0;
  const columns: BarColumn[] = [];
  for (let index = 0; index < count; index += 1) {
    let floor = 0;
    const segments = keys.map((key) => {
      const value = byKey.get(key)?.[index]?.value ?? 0;
      const y0 = stacked ? floor : 0;
      floor = stacked ? floor + value : Math.max(floor, value);
      return { key, value, y0, y1: stacked ? floor : value };
    });
    const first = metric.series[0]?.points[index];
    columns.push({
      t: first?.t ?? "",
      partial: first?.partial ?? false,
      total: stacked ? floor : unsplitValue(metric, segments.length, index),
      segments,
      previous: metric.previous?.points[index]?.value ?? null,
    });
  }
  const tops = columns.flatMap((column) => [
    stacked ? (column.total ?? 0) : Math.max(0, ...column.segments.map((segment) => segment.value)),
    column.previous ?? 0,
  ]);
  return { columns, keys, max: Math.max(0, ...tops), stacked };
}

/** The whole chart in one sentence for a screen reader, before the table: what, how much, and the biggest bucket. */
export function chartSummary(
  name: AnalyticsMetricName,
  metric: StaffAnalyticsMetric,
  interval: AnalyticsInterval,
): string {
  const info = METRIC_INFO[name];
  const total = metric.total.value;
  const phrase = total === null ? "No number" : valueText(total, metric.unit);
  const per = { hour: "hours", day: "days", week: "weeks" }[interval];
  return `${info.label}: ${phrase} in total over ${metric.series[0]?.points.length ?? 0} ${per}.`;
}

/** One line of the tooltip for each series that has a value in the bucket: "2 on iPhone". */
export function breakdownText(column: BarColumn, metric: StaffAnalyticsMetric): readonly string[] {
  if (metric.split === "none") return [];
  return column.segments
    .filter((segment) => segment.value > 0)
    .map(
      (segment) =>
        `${valueText(segment.value, metric.unit)} · ${seriesLabel(metric.split, segment.key)}`,
    );
}
